import Icon from './Icon.jsx';
import { PREMIUM_FEATURES } from '../lib/tiers.js';
import { VERSES } from '../data/verses.js';
import { QUESTIONS } from '../data/questions.js';
import { STORY_TOTAL } from '../data/story.js';

/**
 * The page a stranger sees.
 *
 * Until now there wasn't one. Someone tapping a link landed *inside* the app:
 * a verse they had not asked for, a stat rail reading zero, a level bar at
 * 15/100 XP. That is a product interior, and it asks a first-time visitor to
 * work out what this is from the furniture. Nothing on it said what Scripture
 * Journey does or why an account is worth making.
 *
 * So this screen has one job — the promise, the scope, and a way in — and it
 * is shown to anyone who is not signed in and has not already dismissed it.
 * Dismissing is permanent, which is the line between asking once and nagging.
 *
 * ── One offer, and nothing competing with it ────────────────────────────────
 *
 * This page used to lead with the two free games. They are gone: the page now
 * makes a single case, which is what the trial opens, and the only two buttons
 * are taking it or looking around.
 *
 * That is safe only because /quiz and /before-or-after open their feature
 * directly and are never intercepted. A video promising a playable thing has
 * to link at the thing — if a link promising the character quiz lands here
 * instead, this page is a bait-and-switch and will bounce that traffic.
 *
 * ── On the trial line ───────────────────────────────────────────────────────
 *
 * It says the card is taken and that it charges unless cancelled, in plain
 * words, on the button itself. Competitors advertising "no credit card
 * required" have an easier line to write; this one takes a card, so the only
 * safe thing is to be conspicuous about it. A trial whose terms someone
 * discovers on their statement is a chargeback and a refund request, and it
 * costs more than the signup was worth.
 */
export default function LandingScreen({ trialDays = 7, onStart, onExplore }) {
  return (
    <section className="landing">
      {/* ── The promise ──────────────────────────────────────────────────── */}
      <header className="landing__hero enter" style={{ '--enter-delay': '0ms' }}>
        <span className="landing__mark" aria-hidden="true">
          <Icon name="cross" size={24} />
        </span>
        <h1 className="landing__title">
          The whole Bible story.<br />
          <em>One chapter at a time.</em>
        </h1>
        <p className="landing__sub">
          {STORY_TOTAL} illustrated chapters, {VERSES.length} verses worth memorising and
          {' '}{QUESTIONS.length} questions — free for {trialDays} days.
        </p>

        {/*
          The ask, in the first screenful.
          
          It used to sit at the very bottom, under six benefits, which on a
          phone meant nobody saw it without scrolling — and a good share of the
          people arriving from a video will not scroll at all. The offer has to
          be visible in the moment they land, or the rest of the page is
          writing nobody reads.

          The terms come with it rather than being left to the bottom copy.
          Short here, in full below, but a card being taken is never mentioned
          only once and never only after a scroll.
        */}
        <button
          type="button"
          className="btn btn--primary btn--block btn--lg landing__cta-top"
          onClick={onStart}
        >
          Start your {trialDays} free days
        </button>
        <p className="landing__terms landing__terms--brief">
          Card taken now, nothing charged for {trialDays} days. Cancel before then and you
          pay nothing.
        </p>
        {/*
          A real button, not the underlined footnote this used to be.

          It is the only thing on the page that costs nothing, and it was
          styled like a legal disclaimer — so somebody unwilling to hand over a
          card had, in effect, no option at all and left. Secondary styling
          keeps the trial the obvious choice while making this an actual one.
        */}
        <button type="button" className="btn btn--ghost btn--block landing__explore" onClick={onExplore}>
          Look around first — no card needed
        </button>
      </header>

      {/* ── Proof it is real ─────────────────────────────────────────────── */}
      {/*
        Until now this page was entirely words. A stranger had to take on trust
        that "28 illustrated chapters" meant anything, and the paintings are the
        best thing in the app — showing three of them says more than the
        sentence describing them does.

        Beginning, middle and end of the story, so the strip reads as "all of
        it" without needing a caption. Cropped and re-encoded to about 25KB
        each rather than the 600KB originals: this sits near the top of the
        page and must not delay what is above it. Lazy and async on top of
        that, and the row keeps its height while they load so nothing jumps.
      */}
      <figure className="landing__art enter" style={{ '--enter-delay': '140ms' }}>
        <div className="landing__art-row">
          {[
            ['creation', 'The garden, from the opening chapter'],
            ['sea', 'The sea parting, from Out of Egypt'],
            ['risen', 'The empty tomb, from the final act'],
          ].map(([name, alt]) => (
            <img
              key={name}
              className="landing__art-tile"
              src={`/art/landing/${name}.webp`}
              alt={alt}
              width="420"
              height="420"
              loading="lazy"
              decoding="async"
            />
          ))}
        </div>
        <figcaption>Every chapter is painted, and written from the text itself.</figcaption>
      </figure>

      {/* ── What is actually inside ──────────────────────────────────────── */}
      <div className="landing__inside enter" style={{ '--enter-delay': '180ms' }}>
        <h2 className="landing__h2">What is inside</h2>
        <ul className="landing__list">
          {PREMIUM_FEATURES.map((feature) => (
            <li key={feature.title}>
              <span className="landing__tick" aria-hidden="true">
                <Icon name="check" size={14} />
              </span>
              <span>
                <strong>{feature.title}</strong> — {feature.short ?? feature.body}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* ── The ask ──────────────────────────────────────────────────────── */}
      <div className="landing__cta enter" style={{ '--enter-delay': '260ms' }}>
        {/*
          No second button. The ask is in the first screenful, where it is
          actually seen; repeating it at the bottom of a page most people never
          reach added nothing but a second thing to look at.

          The full terms stay, though. The short line by the button says a card
          is taken and when it charges; this is where it also says the thing
          that only matters after that — it keeps renewing until cancelled.
        */}
        <p className="landing__terms">
          Free for {trialDays} days. A card is taken now and nothing is charged until the
          trial ends — cancel any time before then and you pay nothing. After that
          it renews until you cancel, which you can do from your account in a
          couple of taps.
        </p>

        <p className="landing__legal">
          <a href="/terms" target="_blank" rel="noreferrer">Terms</a>
          <a href="/refunds" target="_blank" rel="noreferrer">Cancelling &amp; refunds</a>
          <a href="/privacy" target="_blank" rel="noreferrer">Privacy</a>
        </p>
      </div>
    </section>
  );
}
